/**
 * Barra de filtros da lista: busca, ordenacao, chips combinaveis (situacao,
 * oferta, canal do ultimo contato, funil), "so alto valor", contagem de
 * filtros ativos e "limpar".
 *
 * Decisoes:
 *  - Todo chip mostra a CONTAGEM da faceta (do periodo inteiro quando o
 *    servidor conta; "nesta pagina" quando as facetas sao parciais). Um chip
 *    com zero nao some: some so quando a dimensao inteira esta vazia.
 *  - Oferta vira `Select` quando ha mais de seis: chips demais viram uma
 *    parede em empresas com catalogo grande.
 *  - O chip de funil so aparece quando a empresa tem os dois funis.
 *  - A ordenacao tem `Select` proprio (acessivel a teclado e ao celular);
 *    os cabecalhos da tabela sao um atalho para a mesma consulta.
 *  - Nada aqui e estado: a barra recebe a consulta e devolve a consulta nova.
 */

import { useId } from 'react';
import { Badge, Button, Checkbox, FilterChips, Icon, Input, Select, type ChipOption } from '../../../components/ui';
import { formatBRLFromCents, formatCount, formatCountCompact } from '../../../lib/format';
import type { FunnelKind } from '../../../types';
import { filtrosAtivos, TUDO } from '../filtros';
import { ORDEM_DAS_SITUACOES, type CanalDoFiltro, type ConsultaDeLeads, type DirecaoDaOrdem, type FacetasDeLeads, type OrdenacaoDeLeads, type SituacaoDoLead } from '../types';
import { CANAL_DO_FILTRO, FUNIL_LABEL, ORDEM_DOS_CANAIS, SITUACAO } from '../vocabulario';

const MAX_CHIPS_DE_OFERTA = 6;

type OrdemComposta = `${OrdenacaoDeLeads}:${DirecaoDaOrdem}`;

const ORDENS: readonly { readonly value: OrdemComposta; readonly label: string }[] = [
  { value: 'abandonedAt:desc', label: 'Saíram há menos tempo' },
  { value: 'abandonedAt:asc', label: 'Saíram há mais tempo' },
  { value: 'amount:desc', label: 'Maior valor' },
  { value: 'amount:asc', label: 'Menor valor' },
  { value: 'situacao:asc', label: 'Situação (fila → encerrados)' },
  { value: 'situacao:desc', label: 'Situação (encerrados → fila)' },
];

function separarOrdem(valor: string): { readonly sort: OrdenacaoDeLeads; readonly dir: DirecaoDaOrdem } {
  const [sort, dir] = valor.split(':');
  const s: OrdenacaoDeLeads = sort === 'amount' ? 'amount' : sort === 'situacao' ? 'situacao' : 'abandonedAt';
  const d: DirecaoDaOrdem = dir === 'asc' ? 'asc' : 'desc';
  return { sort: s, dir: d };
}

export function FiltrosDeLeads({
  consulta,
  facets,
  funis,
  limiarAltoValorCents,
  onChange,
  onLimpar,
  onExportar,
  exportando,
  exportavel,
}: {
  readonly consulta: ConsultaDeLeads;
  readonly facets: FacetasDeLeads;
  readonly funis: readonly FunnelKind[];
  readonly limiarAltoValorCents: number;
  readonly onChange: (parcial: Partial<ConsultaDeLeads>) => void;
  readonly onLimpar: () => void;
  readonly onExportar: () => void;
  /** Selo "CSV gerado" visivel. */
  readonly exportando: boolean;
  readonly exportavel: boolean;
}): JSX.Element {
  const idBusca = useId();
  const idOrdem = useId();
  const ativos = filtrosAtivos(consulta);
  const nota = facets.parciais ? ' (nesta página)' : '';

  const situacoes: readonly ChipOption<string>[] = [
    { value: TUDO, label: 'Tudo', count: formatCountCompact(ORDEM_DAS_SITUACOES.reduce((acc, s) => acc + facets.porSituacao[s], 0)) },
    ...ORDEM_DAS_SITUACOES.filter((s) => facets.porSituacao[s] > 0 || consulta.situacao === s).map((s) => ({
      value: s,
      label: SITUACAO[s].label,
      count: formatCountCompact(facets.porSituacao[s]),
    })),
  ];

  const canais: readonly ChipOption<string>[] = [
    { value: TUDO, label: 'Tudo' },
    ...ORDEM_DOS_CANAIS.filter((c) => facets.porCanal[c] > 0 || consulta.canal === c).map((c) => ({
      value: c,
      label: CANAL_DO_FILTRO[c].label,
      count: formatCountCompact(facets.porCanal[c]),
    })),
  ];

  const ofertasEmChips = facets.porOferta.length <= MAX_CHIPS_DE_OFERTA;
  const ofertas: readonly ChipOption<string>[] = [
    { value: TUDO, label: 'Tudo' },
    ...facets.porOferta.map((o) => ({ value: o.offerId, label: o.name, count: formatCountCompact(o.count) })),
  ];

  const mostrarFunil = funis.length > 1;
  const funisChips: readonly ChipOption<string>[] = [
    { value: TUDO, label: 'Os dois' },
    ...funis.map((f) => ({ value: f, label: FUNIL_LABEL[f], count: formatCountCompact(facets.porFunil[f]) })),
  ];

  const paraSituacao = (v: string): SituacaoDoLead | null => (v === TUDO ? null : ORDEM_DAS_SITUACOES.find((s) => s === v) ?? null);
  const paraCanal = (v: string): CanalDoFiltro | null => (v === TUDO ? null : ORDEM_DOS_CANAIS.find((c) => c === v) ?? null);
  const paraFunil = (v: string): FunnelKind | null => (v === TUDO ? null : funis.find((f) => f === v) ?? null);

  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-line bg-surface px-4 py-4 shadow-soft">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <label htmlFor={idBusca} className="sr-only">
            Buscar por nome, produto ou telefone
          </label>
          <Icon name="busca" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-subtle" />
          <Input id={idBusca} type="search" value={consulta.q} onChange={(e) => onChange({ q: e.target.value })} placeholder="Nome, produto ou final do telefone" autoComplete="off" className="pl-9" />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor={idOrdem} className="flex items-center gap-2 text-xs text-ink-muted">
            <span className="shrink-0">Ordenar</span>
            <Select id={idOrdem} size="sm" value={`${consulta.sort}:${consulta.dir}`} onChange={(e) => onChange(separarOrdem(e.target.value))}>
              {ORDENS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </label>
          <Button variant="secondary" size="md" icon="download" onClick={onExportar} disabled={!exportavel}>
            Exportar CSV
          </Button>
          {exportando ? (
            <Badge tone="good" icon="check">
              CSV gerado
            </Badge>
          ) : null}
        </div>
      </div>

      <FilterChips label={`Filtrar por situação${nota}`} prefix="Situação:" options={situacoes} value={consulta.situacao ?? TUDO} onChange={(v) => onChange({ situacao: paraSituacao(v) })} />

      {facets.porOferta.length > 0 || consulta.offerId !== null ? (
        ofertasEmChips ? (
          <FilterChips label={`Filtrar por oferta${nota}`} prefix="Oferta:" options={ofertas} value={consulta.offerId ?? TUDO} onChange={(v) => onChange({ offerId: v === TUDO ? null : v })} tone="accent" />
        ) : (
          <label className="flex flex-wrap items-center gap-2 text-xs text-ink-muted">
            <span className="mr-1 font-medium text-ink-subtle">Oferta:</span>
            <Select size="sm" value={consulta.offerId ?? TUDO} onChange={(e) => onChange({ offerId: e.target.value === TUDO ? null : e.target.value })} aria-label={`Filtrar por oferta${nota}`} className="max-w-xs">
              <option value={TUDO}>Todas ({formatCount(facets.porOferta.length)} ofertas)</option>
              {facets.porOferta.map((o) => (
                <option key={o.offerId} value={o.offerId}>
                  {o.name} ({formatCount(o.count)})
                </option>
              ))}
            </Select>
          </label>
        )
      ) : null}

      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        <FilterChips label={`Filtrar pelo canal do último contato${nota}`} prefix="Último contato:" options={canais} value={consulta.canal ?? TUDO} onChange={(v) => onChange({ canal: paraCanal(v) })} tone="accent" />
        {mostrarFunil ? <FilterChips label="Filtrar por funil" prefix="Funil:" options={funisChips} value={consulta.funnel ?? TUDO} onChange={(v) => onChange({ funnel: paraFunil(v) })} /> : null}
        <Checkbox
          checked={consulta.altoValor}
          onChange={(v) => onChange({ altoValor: v })}
          label={
            <span className="inline-flex flex-wrap items-center gap-1.5">
              <Icon name="estrela" size="xs" className="text-accent" />
              Só alto valor
              <span className="text-xs text-ink-subtle" title="Percentil 80 dos valores abandonados da empresa: os carrinhos mais caros">
                (a partir de {formatBRLFromCents(limiarAltoValorCents)} · {formatCountCompact(facets.altoValor)}
                {nota})
              </span>
            </span>
          }
        />
      </div>

      {ativos > 0 ? (
        <div className="flex flex-wrap items-center gap-2 border-t border-line pt-3">
          <Badge tone="brand" icon="filtro">
            {ativos} {ativos === 1 ? 'filtro ativo' : 'filtros ativos'}
          </Badge>
          <Button variant="ghost" size="sm" icon="x" onClick={onLimpar}>
            Limpar filtros
          </Button>
        </div>
      ) : null}
    </div>
  );
}
